import { useEffect } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getCustomerInsuranceSummaryAction } from 'actions/getCustomerInsuranceSummaryAction';

import { getConfig } from '../config/configuration';
import { PATH } from '../interfaces/pathTypes';
import { ErrorPage } from './shared/errorPage';
import { ProtectedRoute } from './shared/protectedRoute';
import { Dashboard } from './feature/dashboard';
import { Claims } from './feature/claims';
import { Quotes } from './feature/quotes';
import { Documents } from './feature/documents';
import { Login } from './feature/login';
import { Settings } from './feature/settings';
import { ArrearsPayment } from './feature/arrearsPayment';
import { Footer } from './layout/footer';
import { Header } from './layout/header';
import { ComponentWithModal } from './shared/componentWithModal';
import { ActionModal } from './shared/actionModal';
import { AUTH_TYPE } from '../interfaces/authTypes';
import { isAgentLogin, processAuthLink, useAgentAuthentication } from '../utils';
import { WebchatCardUpdate } from './feature/cardUpdate/webchatCardUpdate';
import { AccountCardUpdate } from './feature/cardUpdate/accountCardUpdate';
import { MakeChanges } from './feature/makeChanges';
import { Conversion } from './feature/conversion';
import { useRumContext } from '../utils/useRumContext';
import { ActionModalType } from 'actions/openModalAction';
import { Audit } from './feature/audit';

const config = getConfig();
const { websiteEndpoint } = config;

function Routes(): JSX.Element {
  const { pathname, search } = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const { isAuthenticated, isAgentAuthenticatedOrCustomer, isAgent } = authentication;
  const isProcessing = useAppSelector((state) => state.customerData.customerInsuranceSummaryLoading);
  const actionModal = useAppSelector((state) => state.policy.actionModal);

  useAgentAuthentication();

  useRumContext();

  useEffect(() => {
    const { path, authToken, urlAuthType } = processAuthLink(pathname);

    if ((!isAgentLogin() || urlAuthType !== AUTH_TYPE.NONE) && !pathname.includes('api/azureAdCallback')) {
      path ? history.replace(`/${path}${search}`) : history.replace(`/dashboard${search}`);
      dispatch(getCustomerInsuranceSummaryAction({ authToken, urlAuthType }));
    }

    const unlisten = history.listen(() => {
      window.scrollTo(0, 0);
    });
    return () => {
      unlisten();
    };
  }, []);

  return (
    <div>
      {actionModal.isOpen && (
        <ActionModal
          show={actionModal.isOpen}
          modalType={actionModal.modalType as ActionModalType}
          alertMessage={actionModal.alertMessage}
        />
      )}
      <Header />
      <main>
        {isAgentAuthenticatedOrCustomer && (
          <Switch>
            <Route path={websiteEndpoint + PATH.LOGIN} component={Login} />
            <Route path={websiteEndpoint + PATH.PAYMENT_MANAGEMENT_WEBCHAT} component={WebchatCardUpdate} />
            <ProtectedRoute
              path={websiteEndpoint + PATH.QUOTES}
              component={Quotes}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.DOCUMENTS}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={Documents}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.SETTINGS}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={Settings}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.ARREARS_PAYMENT}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={ArrearsPayment}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.CLAIMS}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={Claims}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.DASHBOARD}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={Dashboard}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.RENEWAL}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={Conversion}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.PAYMENT_MANAGEMENT}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={AccountCardUpdate}
            />
            <ProtectedRoute
              path={websiteEndpoint + PATH.MAKE_CHANGES}
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
              // @ts-expect-error covered by rest
              childComponent={MakeChanges}
            />
            {isAgent && (
              <ProtectedRoute
                path={websiteEndpoint + PATH.AUDIT}
                component={ComponentWithModal}
                isAuthenticated={isAuthenticated}
                isProcessing={isProcessing}
                // @ts-expect-error covered by rest
                childComponent={Audit}
              />
            )}
            <ProtectedRoute
              path="/"
              component={ComponentWithModal}
              isAuthenticated={isAuthenticated}
              isProcessing={isProcessing}
            />
            <Route component={ErrorPage} />
          </Switch>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default Routes;
